import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Heart, Flag, Eye, EyeOff, Loader2, Trash2, Edit3, X, Check } from 'lucide-react';
import { translationApi } from '../lib/api';

// Language display names
const LANG_NAMES = {
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch',
  it: 'Italiano', pt: 'Português', hi: 'हिन्दी', bn: 'বাংলা',
  zh: '中文', pa: 'ਪੰਜਾਬੀ'
};

export const TranslatableComment = ({ 
  comment, 
  anonId, 
  onLike, 
  onReport,
  onEdit,
  onDelete,
  timeAgo 
}) => {
  const { t, language } = useLanguage();
  
  // Translation state
  const [showOriginal, setShowOriginal] = useState(false);
  const [translatedText, setTranslatedText] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(null);
  const [sourceLang, setSourceLang] = useState(comment.lang || 'en');
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [isSaving, setIsSaving] = useState(false);
  
  const isCommentLiked = (comment.likes || []).includes(anonId);
  const isOwner = comment.author_id === anonId;
  
  // Check if translation is needed
  const commentLang = comment.lang || language;
  const needsTranslation = commentLang !== language;
  
  // Fetch translation when needed
  useEffect(() => {
    if (needsTranslation && !translatedText && !isTranslating && !translationError) {
      fetchTranslation();
    }
  }, [comment.id, language, needsTranslation]);
  
  // Reset translation when language changes
  useEffect(() => {
    setTranslatedText(null);
    setTranslationError(null);
    setShowOriginal(false);
  }, [language]);
  
  // Update editText when comment changes
  useEffect(() => {
    setEditText(comment.text);
  }, [comment.text]);
  
  const fetchTranslation = async () => {
    setIsTranslating(true);
    setTranslationError(null);
    try {
      const response = await translationApi.translateComment(comment.id, language);
      const data = response.data;
      
      if (data.is_original) {
        setTranslatedText(null);
      } else {
        setTranslatedText(data.translated_text);
        setSourceLang(data.source_lang || comment.lang || 'en');
      }
    } catch (err) {
      console.error('Comment translation error:', err);
      const errorMsg = err.response?.data?.detail || 'Translation unavailable';
      setTranslationError(errorMsg);
    } finally {
      setIsTranslating(false);
    }
  };
  
  const handleSaveEdit = async () => {
    if (!editText.trim() || editText === comment.text) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onEdit(comment.id, editText.trim());
      setIsEditing(false);
    } catch (err) {
      console.error('Edit error:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleCancelEdit = () => {
    setEditText(comment.text);
    setIsEditing(false);
  };
  
  // Get display text
  const displayText = (!needsTranslation || showOriginal || !translatedText) 
    ? comment.text 
    : translatedText;

  return (
    <div className="bg-accent/30 rounded-lg p-3" data-testid={`comment-${comment.id}`}>
      {/* Comment Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-[10px] font-medium text-primary">
              {(comment.author_username || 'A').charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-xs font-medium text-foreground">{comment.author_username || 'Anonymous'}</p>
            <p className="text-[10px] text-muted-foreground">
              {timeAgo(comment.created_at)}
              {comment.edited_at && <span className="ml-1">(edited)</span>}
            </p>
          </div>
        </div>
        
        {/* Owner actions (edit/delete) */}
        {isOwner && !isEditing && (
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 rounded hover:bg-accent transition-colors"
              title={t('edit') || 'Edit'}
              data-testid={`comment-edit-${comment.id}`}
            >
              <Edit3 className="w-3 h-3 text-muted-foreground" />
            </button>
            <button
              onClick={() => onDelete(comment.id)}
              className="p-1 rounded hover:bg-destructive/10 transition-colors"
              title={t('delete') || 'Delete'}
              data-testid={`comment-delete-${comment.id}`}
            >
              <Trash2 className="w-3 h-3 text-destructive/70" />
            </button>
          </div>
        )}
      </div>

      {/* Translation loading indicator */}
      {isTranslating && !isEditing && (
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>{t('translating') || 'Translating...'}</span>
        </div>
      )}

      {/* Comment Text / Edit Mode */}
      {isEditing ? (
        <div className="mb-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full px-2 py-1.5 rounded border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            rows={2}
            autoFocus
            data-testid={`comment-edit-input-${comment.id}`}
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={handleSaveEdit}
              disabled={isSaving || !editText.trim()}
              className="flex items-center gap-1 px-2 py-1 rounded bg-primary text-primary-foreground text-xs disabled:opacity-50"
              data-testid={`comment-save-${comment.id}`}
            >
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              <span>{t('save') || 'Save'}</span>
            </button>
            <button
              onClick={handleCancelEdit}
              className="flex items-center gap-1 px-2 py-1 rounded border border-border text-xs hover:bg-accent"
              data-testid={`comment-cancel-${comment.id}`}
            >
              <X className="w-3 h-3" />
              <span>{t('cancel') || 'Cancel'}</span>
            </button>
          </div>
        </div>
      ) : (
        <p className={`text-sm text-foreground/90 mb-2 ${isTranslating ? 'opacity-50' : ''}`}>
          {displayText}
        </p>
      )}
      
      {/* See Original Toggle */}
      {needsTranslation && translatedText && !isTranslating && !isEditing && (
        <button
          onClick={() => setShowOriginal(!showOriginal)}
          className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors mb-2"
          data-testid={`comment-toggle-original-${comment.id}`}
        >
          {showOriginal ? (
            <>
              <EyeOff className="w-2.5 h-2.5" />
              <span>{t('see_translation') || 'See translation'}</span>
            </>
          ) : (
            <>
              <Eye className="w-2.5 h-2.5" />
              <span>{t('see_original') || 'See original'} ({LANG_NAMES[sourceLang] || sourceLang})</span>
            </>
          )}
        </button>
      )}

      {/* Comment Actions */}
      {!isEditing && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => onLike(comment.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
              isCommentLiked
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:bg-accent'
            }`}
            data-testid={`comment-like-${comment.id}`}
          >
            <Heart className={`w-3 h-3 ${isCommentLiked ? 'fill-primary' : ''}`} />
            <span>{comment.likes_count || 0}</span>
          </button>

          <button
            onClick={() => onReport(comment.id)}
            className="flex items-center gap-1 px-2 py-1 rounded text-xs text-muted-foreground hover:bg-accent transition-colors"
            data-testid={`comment-report-${comment.id}`}
          >
            <Flag className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default TranslatableComment;
